import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { upsertWeeklyUpdate, getWeeklyUpdates, changeTaskStatus } from '../utils/api';
import StatusBadge from './StatusBadge';
import PriorityBadge from './PriorityBadge';
import { formatDate } from '../utils/dateHelpers';

const MONTHS = ['Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Sep','Oct','Nov','Dic'];

const pctColor = (p) => p >= 80 ? '#10B981' : p >= 40 ? '#F59E0B' : '#EF4444';
const pctBg   = (p) => p >= 80 ? 'rgba(16,185,129,0.1)' : p >= 40 ? 'rgba(245,158,11,0.1)' : 'rgba(239,68,68,0.08)';

const fmtWeek = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  const fri = new Date(d); fri.setDate(d.getDate() + 6);
  return `${d.getDate()} ${MONTHS[d.getMonth()]} — ${fri.getDate()} ${MONTHS[fri.getMonth()]}`;
};
const fmtDay = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return `${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
};

const getWeekStart = (offset = 0) => {
  const today = new Date();
  const dow = today.getDay();
  const daysSinceMon = (dow + 6) % 7;
  const mon = new Date(today);
  mon.setDate(today.getDate() - daysSinceMon - offset * 7);
  mon.setHours(0, 0, 0, 0);
  return mon.toISOString().split('T')[0];
};

// ─── Arco de progreso circular ────────────────────────────────────────────────
const ProgressArc = ({ pct, size = 80 }) => {
  const r = (size - 10) / 2;
  const circ = 2 * Math.PI * r;
  const offset = circ - (pct / 100) * circ;
  const color = pctColor(pct);
  return (
    <svg width={size} height={size} className="rotate-[-90deg]">
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="rgba(0,0,0,0.07)" strokeWidth={8} />
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={color} strokeWidth={8}
        strokeDasharray={circ} strokeDashoffset={offset}
        strokeLinecap="round" style={{ transition: 'stroke-dashoffset 0.6s ease' }} />
    </svg>
  );
};

// ─── Control % con íconos ─────────────────────────────────────────────────────
const PCT_STEPS = [
  { val: 0,   icon: '○', label: 'Sin inicio' },
  { val: 25,  icon: '◔', label: 'Iniciado' },
  { val: 50,  icon: '◑', label: 'En progreso' },
  { val: 75,  icon: '◕', label: 'Avanzado' },
  { val: 100, icon: '●', label: 'Completo' },
];

const PctSelector = ({ value, onChange }) => {
  const color = pctColor(value || 0);
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-5 gap-2">
        {PCT_STEPS.map(({ val, icon, label }) => {
          const sel = value === val;
          const c = val === 0 ? '#94A3B8' : pctColor(val);
          return (
            <motion.button key={val} whileTap={{ scale: 0.95 }}
              onClick={() => onChange(val)} type="button"
              className="rounded-xl text-center py-3 transition-all"
              style={{
                background: sel ? c + '18' : 'rgba(0,0,0,0.03)',
                border: `2px solid ${sel ? c : 'rgba(0,0,0,0.07)'}`,
                transform: sel ? 'scale(1.04)' : 'scale(1)',
              }}>
              <div className="text-xl">{icon}</div>
              <div className="text-xs font-black mt-0.5" style={{ color: sel ? c : '#94A3B8' }}>{val}%</div>
              <div className="text-[9px] font-medium mt-0.5 leading-tight" style={{ color: sel ? c : '#CBD5E1' }}>{label}</div>
            </motion.button>
          );
        })}
      </div>
      {/* Ajuste fino */}
      <div className="flex items-center gap-3">
        <div className="flex-1 rounded-full overflow-hidden" style={{ height: 4, background: 'rgba(0,0,0,0.07)' }}>
          <motion.div animate={{ width: `${value || 0}%` }} transition={{ duration: 0.3 }}
            style={{ height: '100%', background: color, borderRadius: 9999 }} />
        </div>
        <div className="flex items-center rounded-xl overflow-hidden flex-shrink-0"
          style={{ border: '1.5px solid rgba(0,0,0,0.1)', background: '#F8FAFC' }}>
          <button type="button" onClick={() => onChange(Math.max(0, (value||0) - 5))}
            className="w-8 h-8 flex items-center justify-center text-sm font-bold hover:bg-black/5"
            style={{ color: '#64748B' }}>−</button>
          <input type="number" min={0} max={100} value={value || 0}
            onChange={e => onChange(Math.min(100, Math.max(0, Number(e.target.value))))}
            className="w-10 text-center text-sm font-black focus:outline-none bg-transparent"
            style={{ color }} />
          <button type="button" onClick={() => onChange(Math.min(100, (value||0) + 5))}
            className="w-8 h-8 flex items-center justify-center text-sm font-bold hover:bg-black/5"
            style={{ color: '#64748B' }}>+</button>
        </div>
        <span className="text-sm font-black" style={{ color, minWidth: 32, textAlign: 'right' }}>{value || 0}%</span>
      </div>
    </div>
  );
};

// ─── Card de semana histórica ─────────────────────────────────────────────────
const WeekCard = ({ upd, onEdit }) => {
  const pct = upd.cumplimiento_pct ?? 0;
  const pc  = pctColor(pct);
  const [expanded, setExpanded] = useState(false);
  return (
    <div className="rounded-xl overflow-hidden" style={{ border: `1px solid ${pc}25`, background: '#FAFAFA' }}>
      <div className="flex items-center justify-between px-4 py-3 cursor-pointer"
        style={{ background: pc + '0C', borderBottom: expanded ? `1px solid ${pc}15` : 'none' }}
        onClick={() => setExpanded(e => !e)}>
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
            style={{ background: pc + '20' }}>
            <span className="text-xs font-black" style={{ color: pc }}>{pct}%</span>
          </div>
          <div>
            <p className="text-xs font-black uppercase tracking-wide" style={{ color: pc }}>
              Sem. {fmtWeek(upd.week_start)}
            </p>
            {upd.titulo && <p className="text-xs text-foreground/60 mt-0.5">{upd.titulo}</p>}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {onEdit && (
            <button onClick={e => { e.stopPropagation(); onEdit(upd); }}
              className="text-xs font-bold px-2.5 py-1 rounded-lg"
              style={{ background: 'rgba(99,102,241,0.08)', color: '#6366F1' }}>
              Editar
            </button>
          )}
          <svg className={`w-4 h-4 transition-transform ${expanded ? 'rotate-180' : ''}`}
            style={{ color: '#CBD5E1' }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7"/>
          </svg>
        </div>
      </div>
      {expanded && (
        <div className="px-4 py-3 space-y-2">
          <p className="text-sm text-foreground leading-relaxed">{upd.seguimiento || upd.resultado || '—'}</p>
          {(upd.bloqueo || upd.apoyo_requerido) && (
            <div className="flex gap-2 flex-wrap pt-1">
              {upd.bloqueo && (
                <span className="text-xs px-2 py-1 rounded-full" style={{ background: 'rgba(239,68,68,0.1)', color: '#EF4444' }}>
                  🚫 {upd.bloqueo}
                </span>
              )}
              {upd.apoyo_requerido && (
                <span className="text-xs px-2 py-1 rounded-full" style={{ background: 'rgba(245,158,11,0.1)', color: '#F59E0B' }}>
                  🙋 {upd.apoyo_requerido}
                </span>
              )}
            </div>
          )}
          <div className="w-full rounded-full overflow-hidden mt-2" style={{ height: 3, background: 'rgba(0,0,0,0.06)' }}>
            <div style={{ width: `${pct}%`, height: '100%', background: pc, borderRadius: 9999 }} />
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Drawer principal ─────────────────────────────────────────────────────────
const SeguimientoDrawer = ({ item, weekOffset, onClose, onSaved }) => {
  const { user } = useAuth();
  const [tab, setTab]               = useState('checkin');
  const [updates, setUpdates]       = useState([]);
  const [form, setForm]             = useState(null);
  const [saving, setSaving]         = useState(false);
  const [saved, setSaved]           = useState(false);
  const [editingWeek, setEditingWeek] = useState(null);
  const [closingTask, setClosingTask] = useState(false);
  const textRef = useRef(null);

  const task       = item.task;
  const isOwner    = task.responsible_user_id === user?.id;
  const currentPct = form?.cumplimiento_pct ?? (item.has_update ? (item.update?.cumplimiento_pct ?? task.cumplimiento_pct ?? 0) : (task.cumplimiento_pct ?? 0));
  const color      = pctColor(currentPct);

  const CLOSED = ['Aprobada / Cerrada', 'No realizada'];
  const isClosed = CLOSED.includes(task.status);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    loadUpdates();
    initForm();
    return () => { document.body.style.overflow = ''; };
  }, []);

  useEffect(() => {
    if (tab === 'checkin') setTimeout(() => textRef.current?.focus?.(), 300);
  }, [tab]);

  const loadUpdates = async () => {
    try {
      const r = await getWeeklyUpdates(task.id);
      setUpdates(r.data || []);
    } catch {}
  };

  const initForm = () => {
    const base = item.has_update ? {
      seguimiento: item.update?.seguimiento || item.update?.resultado || '',
      cumplimiento_pct: item.update?.cumplimiento_pct ?? task.cumplimiento_pct ?? 0,
      bloqueo: item.update?.bloqueo || '',
      apoyo_requerido: item.update?.apoyo_requerido || '',
      titulo: item.update?.titulo || '',
    } : {
      seguimiento: '',
      cumplimiento_pct: task.cumplimiento_pct ?? 0,
      bloqueo: '',
      apoyo_requerido: '',
      titulo: '',
    };
    setForm(base);
  };

  const handleEditHistoric = (upd) => {
    setEditingWeek(upd);
    setForm({
      seguimiento: upd.seguimiento || '',
      cumplimiento_pct: upd.cumplimiento_pct ?? 0,
      bloqueo: upd.bloqueo || '',
      apoyo_requerido: upd.apoyo_requerido || '',
      titulo: upd.titulo || '',
      _week_start: upd.week_start,
    });
    setTab('checkin');
  };

  const handleSave = async () => {
    if (!form?.seguimiento?.trim()) return;
    setSaving(true);
    try {
      const payload = {
        titulo: form.titulo || '',
        seguimiento: form.seguimiento,
        cumplimiento_pct: form.cumplimiento_pct ?? 0,
        bloqueo: form.bloqueo || '',
        apoyo_requerido: form.apoyo_requerido || '',
      };
      if (form._week_start) payload.week_start = form._week_start;
      else if (weekOffset > 0) payload.week_start = getWeekStart(weekOffset);
      await upsertWeeklyUpdate(task.id, payload);
      setSaved(true);
      setEditingWeek(null);
      await loadUpdates();
      onSaved?.();
      setTimeout(() => setSaved(false), 2000);
    } catch (e) { alert(e.response?.data?.detail || 'Error al guardar'); }
    setSaving(false);
  };

  const handleCloseTask = async () => {
    setClosingTask(true);
    try {
      await changeTaskStatus(task.id, { status: 'Aprobada / Cerrada' });
      onSaved?.();
      onClose();
    } catch (e) { alert(e.response?.data?.detail || 'Error al cerrar'); }
    setClosingTask(false);
  };

  const weekLabel = editingWeek
    ? `Editando: ${fmtWeek(editingWeek.week_start)}`
    : weekOffset === 0
    ? 'Esta semana'
    : weekOffset === 1
    ? 'Semana anterior'
    : `Hace ${weekOffset} semanas`;

  const TABS = [
    { key: 'checkin',  label: 'Check-in', icon: '✍️' },
    { key: 'historial', label: `Historial ${updates.length > 0 ? `(${updates.length})` : ''}`, icon: '📖' },
    { key: 'info',     label: 'Info',     icon: 'ℹ️' },
  ];

  return (
    <>
      {/* Backdrop */}
      <motion.div key="backdrop" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }} className="fixed inset-0 z-40"
        style={{ background: 'rgba(0,0,0,0.45)', backdropFilter: 'blur(4px)' }}
        onClick={onClose} />

      {/* Panel derecho */}
      <motion.div key="drawer"
        initial={{ x: '100%' }} animate={{ x: 0 }} exit={{ x: '100%' }}
        transition={{ type: 'spring', damping: 30, stiffness: 300, mass: 0.9 }}
        className="fixed right-0 top-0 h-full z-50 flex flex-col"
        style={{ width: '100%', maxWidth: 560, background: '#fff', borderLeft: '1px solid rgba(0,0,0,0.1)',
          boxShadow: '-8px 0 40px rgba(0,0,0,0.12)' }}>

        {/* Header */}
        <div className="flex-shrink-0 px-5 pt-5 pb-4"
          style={{ borderBottom: '1px solid rgba(0,0,0,0.08)', background: '#fff' }}>
          <div className="flex items-start gap-4">
            {/* Arco de progreso */}
            <div className="relative flex-shrink-0">
              <ProgressArc pct={currentPct} size={72} />
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-sm font-black" style={{ color }}>{currentPct}%</span>
              </div>
            </div>

            {/* Info tarea */}
            <div className="flex-1 min-w-0">
              {task.responsabilidad && (
                <p className="text-[10px] font-black uppercase tracking-widest mb-1" style={{ color: '#94A3B8' }}>
                  {task.responsabilidad}
                </p>
              )}
              <h2 className="text-base font-bold leading-snug" style={{ color: '#0F172A' }}>
                {task.title}
              </h2>
              <div className="flex flex-wrap gap-1.5 mt-2">
                <StatusBadge status={task.status} />
                <PriorityBadge priority={task.priority} />
              </div>
            </div>

            {/* Cerrar */}
            <button onClick={onClose}
              className="flex-shrink-0 w-9 h-9 rounded-xl flex items-center justify-center"
              style={{ background: 'rgba(0,0,0,0.05)', color: '#64748B' }}>
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"/>
              </svg>
            </button>
          </div>

          {/* Semana */}
          <div className="mt-3 flex items-center gap-2">
            <span className="text-xs font-bold px-2.5 py-1 rounded-lg"
              style={{ background: editingWeek ? 'rgba(99,102,241,0.08)' : 'rgba(0,208,132,0.08)',
                color: editingWeek ? '#6366F1' : '#00A368' }}>
              {editingWeek ? '✎ ' : '📅 '}{weekLabel}
            </span>
            {editingWeek && (
              <button onClick={() => { setEditingWeek(null); initForm(); }}
                className="text-xs font-bold" style={{ color: '#6366F1' }}>
                × Cancelar edición
              </button>
            )}
          </div>

          {/* Tabs */}
          <div className="flex gap-1 mt-3 p-1 rounded-xl" style={{ background: 'rgba(0,0,0,0.04)' }}>
            {TABS.map(t => (
              <button key={t.key} onClick={() => setTab(t.key)}
                className="flex-1 py-2 rounded-lg text-xs font-bold transition-all"
                style={{
                  background: tab === t.key ? '#fff' : 'transparent',
                  color: tab === t.key ? '#0F172A' : '#94A3B8',
                  boxShadow: tab === t.key ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                }}>
                {t.icon} {t.label}
              </button>
            ))}
          </div>
        </div>

        {/* Contenido scrollable */}
        <div className="flex-1 overflow-y-auto">

          {/* ── CHECK-IN ─────────────────────────────────────────────────── */}
          {tab === 'checkin' && (
            <div className="px-5 py-5 space-y-5">
              {!isOwner ? (
                <div className="rounded-xl p-4 text-center"
                  style={{ background: 'rgba(0,0,0,0.03)', border: '1px solid rgba(0,0,0,0.07)' }}>
                  <p className="text-sm font-semibold" style={{ color: '#64748B' }}>
                    Solo el responsable puede registrar el seguimiento.
                  </p>
                </div>
              ) : (
                <>
                  {/* Cumplimiento */}
                  <div>
                    <p className="text-[10px] font-black uppercase tracking-widest mb-3" style={{ color: '#94A3B8' }}>
                      Cumplimiento
                    </p>
                    <PctSelector value={form?.cumplimiento_pct ?? 0}
                      onChange={v => setForm(f => ({ ...f, cumplimiento_pct: v }))} />
                  </div>

                  {/* Seguimiento */}
                  <div>
                    <p className="text-[10px] font-black uppercase tracking-widest mb-2" style={{ color: '#94A3B8' }}>
                      Seguimiento <span style={{ color: '#EF4444' }}>*</span>
                    </p>
                    <textarea ref={textRef} rows={5}
                      value={form?.seguimiento || ''}
                      onChange={e => setForm(f => ({ ...f, seguimiento: e.target.value }))}
                      placeholder="¿Qué avanzó esta semana? Describe logros, decisiones, próximos pasos..."
                      className="w-full resize-none rounded-xl px-4 py-3 focus:outline-none"
                      style={{
                        background: '#F8FAFC',
                        border: `1.5px solid ${form?.seguimiento?.trim() ? 'rgba(0,208,132,0.4)' : 'rgba(0,0,0,0.08)'}`,
                        color: '#1E293B', fontSize: 15, lineHeight: 1.7, transition: 'border-color 0.2s',
                      }} />
                  </div>

                  {/* Bloqueo + Apoyo */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <p className="text-[10px] font-black uppercase tracking-widest mb-2" style={{ color: '#94A3B8' }}>
                        🚫 Bloqueo
                      </p>
                      <input type="text" value={form?.bloqueo || ''}
                        onChange={e => setForm(f => ({ ...f, bloqueo: e.target.value }))}
                        placeholder="¿Qué frena el avance?"
                        className="w-full rounded-xl px-3.5 py-3 focus:outline-none text-sm"
                        style={{ background: '#F8FAFC', border: '1.5px solid rgba(0,0,0,0.07)', color: '#1E293B' }} />
                    </div>
                    <div>
                      <p className="text-[10px] font-black uppercase tracking-widest mb-2" style={{ color: '#94A3B8' }}>
                        🙋 Apoyo
                      </p>
                      <input type="text" value={form?.apoyo_requerido || ''}
                        onChange={e => setForm(f => ({ ...f, apoyo_requerido: e.target.value }))}
                        placeholder="¿Qué necesitas?"
                        className="w-full rounded-xl px-3.5 py-3 focus:outline-none text-sm"
                        style={{ background: '#F8FAFC', border: '1.5px solid rgba(0,0,0,0.07)', color: '#1E293B' }} />
                    </div>
                  </div>

                  {/* Cerrar tarea si 100% */}
                  {currentPct === 100 && !isClosed && (
                    <div className="rounded-xl p-4 space-y-3"
                      style={{ background: 'rgba(16,185,129,0.06)', border: '1.5px solid rgba(16,185,129,0.25)' }}>
                      <div className="flex items-start gap-3">
                        <span className="text-2xl">🎉</span>
                        <div>
                          <p className="text-sm font-black" style={{ color: '#10B981' }}>¡Tarea al 100%!</p>
                          <p className="text-xs mt-0.5" style={{ color: '#64748B' }}>
                            Puedes cerrar formalmente esta tarea como completada.
                          </p>
                        </div>
                      </div>
                      <button onClick={handleCloseTask} disabled={closingTask}
                        className="w-full rounded-xl font-bold text-sm py-2.5 flex items-center justify-center gap-2"
                        style={{ background: '#10B981', color: '#fff', opacity: closingTask ? 0.6 : 1 }}>
                        {closingTask ? 'Cerrando...' : '✓ Marcar como Aprobada / Cerrada'}
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* ── HISTORIAL ────────────────────────────────────────────────── */}
          {tab === 'historial' && (
            <div className="px-5 py-5">
              {updates.length === 0 ? (
                <div className="text-center py-12">
                  <p className="text-4xl mb-3">📅</p>
                  <p className="text-sm font-semibold" style={{ color: '#64748B' }}>Sin historial de seguimiento</p>
                  <p className="text-xs mt-1" style={{ color: '#CBD5E1' }}>Los check-ins semanales aparecerán aquí</p>
                </div>
              ) : (
                <div className="space-y-3">
                  <p className="text-[10px] font-black uppercase tracking-widest mb-4" style={{ color: '#94A3B8' }}>
                    {updates.length} semana{updates.length !== 1 ? 's' : ''} de seguimiento
                  </p>

                  {/* Línea de tiempo resumida */}
                  <div className="flex items-end gap-1 mb-5 px-1" style={{ height: 64 }}>
                    {updates.slice().reverse().map((upd, i) => {
                      const p = upd.cumplimiento_pct ?? 0;
                      const pc = p === 0 ? '#E2E8F0' : pctColor(p);
                      const h = Math.max(4, (p / 100) * 52);
                      return (
                        <div key={i} className="flex-1 rounded-t-sm transition-all cursor-pointer"
                          style={{ height: h, background: pc, minWidth: 4 }}
                          title={`${fmtWeek(upd.week_start)}: ${p}%`} />
                      );
                    })}
                  </div>

                  {updates.map((upd, i) => (
                    <WeekCard key={upd.id || i} upd={upd}
                      onEdit={isOwner ? handleEditHistoric : null} />
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ── INFO ─────────────────────────────────────────────────────── */}
          {tab === 'info' && (
            <div className="px-5 py-5 space-y-5">
              {/* Objetivo */}
              {task.objetivo && (
                <div className="rounded-xl p-4"
                  style={{ background: 'rgba(0,208,132,0.06)', border: '1px solid rgba(0,208,132,0.15)' }}>
                  <p className="text-[10px] font-black uppercase tracking-widest mb-2" style={{ color: '#00D084' }}>Objetivo</p>
                  <p className="text-sm leading-relaxed" style={{ color: '#1E293B' }}>{task.objetivo}</p>
                </div>
              )}
              {/* Meta */}
              {task.meta && (
                <div className="rounded-xl p-4"
                  style={{ background: 'rgba(99,102,241,0.06)', border: '1px solid rgba(99,102,241,0.15)' }}>
                  <p className="text-[10px] font-black uppercase tracking-widest mb-2" style={{ color: '#6366F1' }}>Meta</p>
                  <p className="text-sm leading-relaxed" style={{ color: '#1E293B' }}>{task.meta}</p>
                </div>
              )}
              {/* Descripción */}
              {task.description && (
                <div>
                  <p className="text-[10px] font-black uppercase tracking-widest mb-2" style={{ color: '#94A3B8' }}>Descripción</p>
                  <p className="text-sm leading-relaxed whitespace-pre-wrap" style={{ color: '#334155' }}>{task.description}</p>
                </div>
              )}
              {/* Fechas + datos */}
              <div className="grid grid-cols-2 gap-3">
                {task.due_date && (
                  <div className="rounded-xl p-3" style={{ background: '#F8FAFC', border: '1px solid rgba(0,0,0,0.07)' }}>
                    <p className="text-[10px] font-black uppercase tracking-widest mb-1" style={{ color: '#94A3B8' }}>Fecha límite</p>
                    <p className="text-sm font-semibold" style={{ color: '#0F172A' }}>{formatDate(task.due_date)}</p>
                  </div>
                )}
                <div className="rounded-xl p-3" style={{ background: '#F8FAFC', border: '1px solid rgba(0,0,0,0.07)' }}>
                  <p className="text-[10px] font-black uppercase tracking-widest mb-1" style={{ color: '#94A3B8' }}>Creada</p>
                  <p className="text-sm font-semibold" style={{ color: '#0F172A' }}>{formatDate(task.created_at)}</p>
                </div>
                {task.priority && (
                  <div className="rounded-xl p-3" style={{ background: '#F8FAFC', border: '1px solid rgba(0,0,0,0.07)' }}>
                    <p className="text-[10px] font-black uppercase tracking-widest mb-1" style={{ color: '#94A3B8' }}>Prioridad</p>
                    <p className="text-sm font-semibold" style={{ color: '#0F172A' }}>{task.priority}</p>
                  </div>
                )}
                <div className="rounded-xl p-3" style={{ background: '#F8FAFC', border: '1px solid rgba(0,0,0,0.07)' }}>
                  <p className="text-[10px] font-black uppercase tracking-widest mb-1" style={{ color: '#94A3B8' }}>Cumplimiento</p>
                  <p className="text-lg font-black" style={{ color: pctColor(task.cumplimiento_pct || 0) }}>
                    {task.cumplimiento_pct || 0}%
                  </p>
                </div>
              </div>
              {/* Checklist */}
              {task.checklist?.length > 0 && (
                <div>
                  <p className="text-[10px] font-black uppercase tracking-widest mb-3" style={{ color: '#94A3B8' }}>
                    Checklist ({task.checklist.filter(c => c.done).length}/{task.checklist.length})
                  </p>
                  <div className="space-y-2">
                    {task.checklist.map((c, i) => (
                      <div key={i} className="flex items-center gap-3 py-1">
                        <div className="w-5 h-5 rounded-full flex-shrink-0 flex items-center justify-center"
                          style={{ background: c.done ? 'rgba(16,185,129,0.12)' : 'rgba(0,0,0,0.06)' }}>
                          {c.done && <svg className="w-3 h-3" style={{ color: '#10B981' }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7"/>
                          </svg>}
                        </div>
                        <p className="text-sm" style={{ color: c.done ? '#94A3B8' : '#334155',
                          textDecoration: c.done ? 'line-through' : 'none' }}>{c.text}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer guardar (solo en tab check-in) */}
        {tab === 'checkin' && isOwner && (
          <div className="flex-shrink-0 px-5 pt-3 pb-6"
            style={{ borderTop: '1px solid rgba(0,0,0,0.07)', background: '#fff' }}>
            <motion.button whileTap={{ scale: 0.97 }} onClick={handleSave}
              disabled={saving || !form?.seguimiento?.trim()}
              className="w-full rounded-2xl font-black flex items-center justify-center gap-2 disabled:opacity-40"
              style={{
                height: 54, fontSize: 16,
                background: !form?.seguimiento?.trim()
                  ? '#F1F5F9'
                  : saved
                  ? 'rgba(16,185,129,0.12)'
                  : 'linear-gradient(135deg,#00D084,#00C878)',
                color: !form?.seguimiento?.trim() ? '#CBD5E1' : saved ? '#10B981' : '#003D24',
              }}>
              {saving ? (
                <><div className="w-4 h-4 border-2 border-current/30 border-t-current rounded-full animate-spin" />Guardando...</>
              ) : saved ? (
                <><svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7"/>
                  </svg>¡Guardado correctamente!</>
              ) : editingWeek
                ? `Actualizar semana (${fmtWeek(editingWeek.week_start)})`
                : item.has_update ? 'Actualizar seguimiento' : 'Guardar seguimiento'}
            </motion.button>
          </div>
        )}
      </motion.div>
    </>
  );
};

export default SeguimientoDrawer;
